/**
 * Trovva — Enroll Action Plan Modal
 *
 * Modal to let an agent enroll a contact into an action plan.
 * Fetches available templates from /api/action-plans?action=list_templates
 * Submits enrollment via POST to /api/action-plans with { action: 'enroll', template_id, contact_id }
 *
 * Usage:
 *   <EnrollActionPlanModal
 *     contactId="123"
 *     contactName="Jane Doe"
 *     onClose={() => {...}}
 *     onEnrolled={() => {...}}
 *     showToast={(msg, type) => {...}}
 *   />
 */

import { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';

async function getToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token;
}

export default function EnrollActionPlanModal({ contactId, contactName, onClose, onEnrolled, showToast }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      const token = await getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const r = await fetch('/api/action-plans?action=list_templates', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (r.ok) {
          setPlans(data.templates || []);
        } else {
          showToast?.('Failed to load action plans', 'error');
        }
      } catch (e) {
        console.error('Failed to fetch action plans:', e);
        showToast?.('Error loading action plans', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [showToast]);

  const handleEnroll = async (plan) => {
    setEnrolling(true);
    const token = await getToken();
    if (!token) {
      setEnrolling(false);
      return;
    }
    try {
      const r = await fetch('/api/action-plans', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'enroll',
          template_id: plan.id,
          contact_id: contactId,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        showToast?.(`Enrolled ${contactName} in "${plan.name}"`, 'success');
        onEnrolled?.();
        onClose?.();
      } else {
        showToast?.(data.error || 'Failed to enroll in action plan', 'error');
      }
    } catch (e) {
      console.error('Error enrolling in action plan:', e);
      showToast?.('Error enrolling in action plan', 'error');
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.colors.card, borderRadius: 14, padding: 22,
        width: '100%', maxWidth: 480, boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, color: theme.colors.text }}>
              Enroll <strong style={{ color: theme.colors.primary }}>{contactName}</strong>
            </h3>
            <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>
              Choose an action plan to start
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: theme.colors.textLight, padding: 4, fontSize: 16, lineHeight: 1,
          }}><X size={16} /></button>
        </div>

        {loading && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            minHeight: 200, color: theme.colors.textLight,
          }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {!loading && plans.length === 0 && (
          <div style={{
            padding: 20, textAlign: 'center', color: theme.colors.textLight, fontSize: 13,
          }}>
            <div style={{ marginBottom: 8 }}>No action plans configured yet.</div>
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
              Create templates in Settings → Action Plans to get started.
            </div>
          </div>
        )}

        {!loading && plans.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 380, overflowY: 'auto' }}>
            {plans.map(plan => (
              <div key={plan.id} style={{
                padding: 12, border: `1px solid ${theme.colors.border}`, borderRadius: 10,
                background: theme.colors.card, cursor: 'pointer',
                transition: 'all 0.2s',
                borderColor: selectedPlan?.id === plan.id ? theme.colors.primary : theme.colors.border,
                background: selectedPlan?.id === plan.id ? theme.colors.bg : theme.colors.card,
              }} onClick={() => setSelectedPlan(plan)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
                      {plan.name}
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 4 }}>
                      {plan.description}
                    </div>
                    <div style={{ fontSize: 11, color: theme.colors.primary }}>
                      {plan.step_count || 0} steps
                    </div>
                  </div>
                  <button
                    disabled={enrolling}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEnroll(plan);
                    }}
                    style={{
                      padding: '7px 12px', borderRadius: 8, border: 'none',
                      background: theme.colors.primary, color: 'white',
                      fontSize: 11, fontWeight: 600, cursor: enrolling ? 'not-allowed' : 'pointer',
                      opacity: enrolling ? 0.6 : 1,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      whiteSpace: 'nowrap', marginLeft: 8,
                    }}
                  >
                    <CheckCircle2 size={12} />
                    {enrolling ? 'Enrolling…' : 'Enroll'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '9px 14px', borderRadius: 10,
            background: 'transparent', border: `1px solid ${theme.colors.border}`,
            color: theme.colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
